import { useMemo, useRef } from 'react';
import useInfiniteStaticProducts from 'hooks/useInfiniteStaticProducts';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import { useDeferredValue } from '../../../hooks/reactShims';
import { getVariantsByMatchedColorFirst, buildProductsInAlgoliaOrder } from '../utils';

/**
 * Derives product IDs from Algolia hits, fetches full product data, maps
 * variant highlighting, orders results to match Algolia ranking, and maintains
 * the previously-visible product list to prevent flash/blink on filter changes.
 */
const useDisplayProducts = ({
  items,
  searchBoxQuery,
  results,
  seed
}) => {
  const previousProductsRef = useRef([]);
  const {
    selectedColors
  } = useAdvancedSearchContext();
  const selectedColorLabels = useMemo(() => Object.entries(selectedColors || {}).filter(([, isSelected]) => isSelected).map(([color]) => color), [selectedColors]);

  // Prefer SearchBox query – results.query can be stale/empty when filters
  // change (e.g. ready to ship)
  const searchKey = useMemo(() => {
    const query = searchBoxQuery || results?.query || '';
    const params = results?.params || '';
    const paramsWithoutPage = params.split('&').filter(param => !param.startsWith('page=')).join('&');
    return `${query}-${paramsWithoutPage}`;
  }, [searchBoxQuery, results?.query, results?.params]);
  const productIdsRaw = useMemo(() => {
    if (!items?.length) return [];
    return items.map(hit => {
      const id = hit.product_id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id_0 => id_0 > 0 && !Number.isNaN(id_0));
  }, [items]);

  // Defer product IDs so we don't trigger immediate re-fetches while typing
  const productIds = useDeferredValue(productIdsRaw);
  const {
    products: productsWithVariantsToShow,
    loading
  } = useInfiniteStaticProducts({
    productIds,
    searchKey,
    seed
  }, {
    skipSorting: true
  });
  const productsWithHighlightedVariantsFirst = useMemo(() => productsWithVariantsToShow.map(product => ({
    ...product,
    variantsToShow: getVariantsByMatchedColorFirst(product, items, selectedColorLabels)
  })), [productsWithVariantsToShow, items, selectedColorLabels]);
  const productsInAlgoliaOrder = useMemo(() => buildProductsInAlgoliaOrder(productsWithHighlightedVariantsFirst, items), [items, productsWithHighlightedVariantsFirst]);

  // Keep previous products visible until new ones are ready (prevents
  // flash/blink). When Algolia returns 0 hits, show nothing (no results).
  const displayProducts = useMemo(() => {
    if (!items?.length) {
      previousProductsRef.current = [];
      return [];
    }
    if (productsInAlgoliaOrder?.length > 0) {
      previousProductsRef.current = productsInAlgoliaOrder;
      return productsInAlgoliaOrder;
    }
    return previousProductsRef.current;
  }, [items?.length, productsInAlgoliaOrder]);
  return {
    displayProducts,
    loading,
    productIds,
    productIdsRaw
  };
};
export default useDisplayProducts;